// Phase 6.AJ — Ticker provenance helper
// ---------------------------------------------------------------------------
// Stashes the EXACT thing a user typed/clicked/tapped just before being
// navigated to /analyze/:ticker. Analyze.js reads this on mount and
// includes it in lifecycle telemetry so the Owner Console can prove
// "the user typed MU and ended up on the MU analyze page" — or, if the
// values diverge, surface the mismatch explicitly.
//
// All entries are kept in sessionStorage (cleared at tab close) under a
// single key so the latest action always wins. We deliberately use a
// per-tab store to avoid cross-tab contamination.

const KEY = "stockaily.ticker_provenance";

export function stashTickerProvenance({
  typed = "",
  target = "",
  source = "unknown",
  // Phase 6.BE — split typed vs selected so company-name searches do
  // not generate false mismatches against the canonical ticker.
  // `selected_label` is the human-readable label clicked on (e.g.
  // "Micron Technology, Inc."), and `selected_symbol` is the ticker
  // the suggestion resolved to (e.g. "MU"). When `selected_symbol`
  // matches `target`, no mismatch is reported even if `typed`
  // differs (e.g. typed="Micron").
  selected_label = "",
  selected_symbol = "",
} = {}) {
  try {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(
      KEY,
      JSON.stringify({
        typed: String(typed || "").trim(),
        typed_uppercase: String(typed || "").trim().toUpperCase(),
        target: String(target || "").trim().toUpperCase(),
        // Phase 6.BE
        selected_label: String(selected_label || "").trim(),
        selected_symbol: String(selected_symbol || "").trim().toUpperCase(),
        source,
        ts: Date.now(),
        from_location: window.location.pathname || null,
      })
    );
  } catch {
    /* swallow — telemetry never breaks UX */
  }
}

export function readTickerProvenance() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Treat anything older than 60s as stale — the user navigated some
    // time ago and the action is no longer attributable to this analyze.
    if (!parsed || (Date.now() - (parsed.ts || 0)) > 60_000) return null;
    return parsed;
  } catch {
    return null;
  }
}
